// THE EVIDENCE CORPUS (DESIGN §6; tab-documents/RESEARCH.md + IMPLEMENTATION.md).
//
// The falsifiable statement this page is built to satisfy:
//
//   The Documents tab is the ledger of everything a requirement in this engagement is allowed
//   to cite. The operator arrives with exactly one of three questions — (a) I have just dropped
//   N files in, what still needs me before the agent can read them? (b) which of these documents
//   has actually given the register anything, and which is dark? (c) where is the document that
//   says X, and is it the current version of it? They must be able to discharge every hold on
//   the corpus AS A SET, decide every duplicate pair WITHOUT LEAVING EITHER HALF, and see, per
//   document, what it has yielded.
//
// Five zones, in the ClientPage idiom: masthead · the instrument rail · HOLDS · CORPUS · panel.
//
// What the audit log said about the old page, and what changed:
//   · 25 × document.confirm_date in 20 seconds, 0.55 s apart, in grid order — a ceremony the
//     interface forced. Now one HOLD card, one drawer, one `Confirm N ·writes`.
//   · 1 × dismiss_near_duplicate against ~11 open hints in four days — because the hint is
//     written on ONE half of the pair and the other half showed nothing. Now both halves know,
//     and the pair is decided side by side without opening either document.
//   · the word "evidence" did not appear on this page at all. Now YIELD is a rail cell, a grid
//     column and the panel's FIRST section.
//   · the server's full-text search existed and was never called. Now it is, and the result
//     says where it landed.

import {
  useCallback, useEffect, useMemo, useRef, useState,
  type DragEvent, type ReactNode,
} from 'react';
import {
  Upload, FilePlus2, Download, FileText, Library, ScanText, X, Check,
  Trash2, Unlink, RefreshCw,
} from 'lucide-react';

import { useEngagement } from '../context/EngagementContext.js';
import { Tx, TxTools } from '../components/Tx.js';
import { useToast } from '../components/Toast.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState from '../components/EmptyState.js';
import { ApiError } from '../api/client.js';
import { usePoll, POLL_MS } from '../hooks/usePoll.js';
import { ROUTES, useOpenRoute, useDeepLink, useRouteParams } from '../routes.js';
import { degradedRail, type RailCell } from '../../shared/rail.js';
import { useT, useLocale } from '../i18n/index.js';
import { tCtx } from '../../shared/i18n.js';
import './DocumentsPage.de.js';
import './DocumentsPage.fr.js';
import {
  documentsApi, DOCUMENT_FOLDERS,
  type DocumentRow, type DocumentDetail, type DocumentDeletionPlan, type UploadOutcome,
  type SourceType, type DocumentFolder, type LibraryDocumentRow, type RescanReport,
  type DocumentEvidence,
} from '../api/documents.js';

const STATUS_POLL_MS = 4000;
const PREVIEW_CHARS = 5000;
const SEARCH_DEBOUNCE_MS = 300;
const EVIDENCE_SHOWN = 4;

const ACCEPT = [
  '.txt', '.md', '.markdown', '.json', '.csv', '.log',
  '.vtt', '.srt', '.pdf', '.docx', '.doc', '.xlsx', '.xls', '.pptx', '.ppt',
  '.png', '.jpg', '.jpeg', '.webp', '.eml',
].join(',');

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** The rail: fixed set, fixed order, invariant geometry. Every cell renders in every state. */
type DocsRailKey = 'CORPUS' | 'DATED' | 'READ' | 'PAIRS' | 'YIELD';
const RAIL_KEYS: readonly DocsRailKey[] = ['CORPUS', 'DATED', 'READ', 'PAIRS', 'YIELD'] as const;
interface DocsRailCell extends RailCell { key: DocsRailKey }

/** What a rail cell filters the grid to. CORPUS filters nothing — it IS the corpus. */
type RailFilter = '' | 'undated' | 'unread' | 'pairs' | 'dark';
const RAIL_FILTER: Record<DocsRailKey, RailFilter> = {
  CORPUS: '', DATED: 'undated', READ: 'unread', PAIRS: 'pairs', YIELD: 'dark',
};

const TONE_CLASS: Record<string, string> = {
  ink: '', warn: ' fv--warn', attend: ' fv--warn',
  ok: ' fv--ok', err: ' fv--err', broken: ' fv--err',
  nil: ' fv--nil', unknown: ' fv--nil',
};

// ── row-level facts ─────────────────────────────────────────────────────────

function formatBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** A near-duplicate hint that has been acted on stops being a question. */
const hasOpenNearDuplicate = (d: DocumentRow): boolean =>
  !!d.meta?.near_duplicate_hint && !d.near_duplicate_resolved;

/** …and the OTHER half of the pair only knows through the server's inbound projection. */
const inPair = (d: DocumentRow): boolean =>
  hasOpenNearDuplicate(d) || (d.near_duplicate_of?.length ?? 0) > 0;

const duplicateCount = (d: DocumentRow): number => d.meta?.duplicate_uploads?.length ?? 0;

/** Mails carry attachments; only they can be re-scanned as supported formats grow. */
const isEmailDoc = (d: DocumentRow): boolean =>
  d.meta?.kind === 'email' || d.mime_type === 'message/rfc822'
  || d.original_filename.toLowerCase().endsWith('.eml');

/** Mirrors OCR_EXTENSIONS on the server — the action is offered only where it can work. */
const OCR_READABLE = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'tif', 'tiff', 'pdf', 'pptx', 'ppt']);
const canOcrFile = (filename: string): boolean =>
  OCR_READABLE.has((filename.split('.').pop() ?? '').toLowerCase());

const spansOf = (d: DocumentRow): number => d.cited_by_evidence ?? 0;
const isLive = (d: DocumentRow): boolean => d.status !== 'superseded';
const monthOf = (d: DocumentRow): string =>
  d.effective_date ? `${MONTHS[Number(d.effective_date.slice(5, 7)) - 1]} ${d.effective_date.slice(0, 4)}` : 'No effective date';

/** One near-duplicate pair, de-duplicated across both directions. */
interface Pair {
  key: string;
  /** The document carrying the hint — the one whose `dismiss` and `supersede` verbs apply. */
  a: DocumentRow;
  bRef: string;
  bId: number;
  b: DocumentRow | null;
  kind: string;
  reason: string;
}

/**
 * A hold, in the shape every tab ladder emits (UNIFIED-FUNCTION F3): a rule name, a weight, a
 * title, a consequence sentence. Declared locally rather than imported from
 * `src/server/utils/queueRules.ts` — no file under src/client imports from src/server, and
 * `queueRules` would drag the server realm into the browser bundle. The ORDER is the contract:
 * weight ASC, then rule name.
 */
interface Hold {
  rule: string;
  weight: number;
  title: ReactNode;
  why: ReactNode;
  cta: ReactNode;
  drawer?: ReactNode;
  tone?: 'attend' | 'quiet' | 'done';
}

/** The ATTEST claim mark for a requirement citing this document. */
const claimMark = (status: string): string =>
  status === 'draft' || status === 'proposed' ? 'att att--proposed' : 'att att--held';

export default function DocumentsPage() {
  const nav = useEngagement();
  const { current } = nav;
  const openRoute = useOpenRoute(nav);
  const routeParams = useRouteParams();
  const toast = useToast();
  const t = useT();
  const { tp, fmtDate, fmtDateTime, fmtNum } = useLocale();

  const engagementId = current?.id ?? null;

  // ── corpus ────────────────────────────────────────────────────────────────
  // One unfiltered fetch. Every count on this page — the rail, the holds, the facets — is a
  // fact about the CORPUS, not about the current filter, so the filters are applied to the
  // rows in hand and never to the request. (The one exception is `q`: only the server can see
  // extracted_text, so a text search is a second, separate request — below.)
  const load = useCallback(async () => {
    if (!engagementId) return { engagement_id: 0, rows: [] as DocumentRow[] };
    return { engagement_id: engagementId, rows: await documentsApi.list(engagementId) };
  }, [engagementId]);

  const corpus = usePoll(load, POLL_MS);
  const docs = useMemo(
    () => (corpus.data && corpus.data.engagement_id === engagementId ? corpus.data.rows : []),
    [corpus.data, engagementId],
  );
  const refresh = corpus.refresh;

  // ── out-of-band queues (OCR, librarian) ───────────────────────────────────
  const [ocrQueue, setOcrQueue] = useState<Set<number>>(new Set());
  const [ocrUp, setOcrUp] = useState<{ available: boolean; reason: string | null } | null>(null);
  const [libQueue, setLibQueue] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!engagementId) return;
    let stop = false;
    const tick = async () => {
      try {
        const s = await documentsApi.ocrStatus();
        if (stop) return;
        setOcrUp({ available: s.available, reason: s.reason });
        const next = new Set(s.pending.map((p) => p.document_id));
        setOcrQueue((prev) => { if (prev.size && next.size < prev.size) void refresh(); return next; });
      } catch { /* the status endpoint is advisory; a blip must not break the page */ }
      try {
        const s = await documentsApi.librarianStatus();
        if (stop) return;
        const next = new Set(s.pending.map((p) => p.document_id));
        setLibQueue((prev) => { if (prev.size && next.size < prev.size) void refresh(); return next; });
      } catch { /* advisory too */ }
    };
    void tick();
    const t = setInterval(tick, STATUS_POLL_MS);
    return () => { stop = true; clearInterval(t); };
  }, [engagementId, refresh]);

  // ── filters (page-local, gone on unmount — B4) ────────────────────────────
  const [railFilter, setRailFilter] = useState<RailFilter>('');
  const [folder, setFolder] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'yield' | 'ingest'>('date');
  const [search, setSearch] = useState('');
  const [serverQ, setServerQ] = useState('');
  const [textHits, setTextHits] = useState<Set<number> | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  // Arrival intents, applied once (F1 law 2). `status` is the only one this page has ever
  // emitted; the rest of its filters are page-local by design.
  useDeepLink(routeParams, { status: (v) => setStatusFilter(v) });

  useEffect(() => {
    const t = setTimeout(() => setServerQ(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // THE SEARCH. `documentsApi.list()` has always accepted `q` and the server has always ANDed
  // it across filename, ref, summary, tags and the extracted text — the page simply never sent
  // it, and re-implemented a weaker match over ref + filename + authored_by (which is NULL on
  // every URSC document). Typing "licensing" returned nothing while the word sat thirty times
  // in DOC-030's text. This is that request.
  useEffect(() => {
    if (!engagementId || !serverQ) { setTextHits(null); setSearchError(null); return; }
    let stop = false;
    setSearching(true);
    documentsApi.list(engagementId, { q: serverQ })
      .then((rows) => { if (!stop) { setTextHits(new Set(rows.map((r) => r.id))); setSearchError(null); } })
      .catch((err) => { if (!stop) { setTextHits(null); setSearchError(err instanceof Error ? err.message : String(err)); } })
      .finally(() => { if (!stop) setSearching(false); });
    return () => { stop = true; };
  }, [engagementId, serverQ]);

  const clearFilters = useCallback(() => {
    setRailFilter(''); setFolder(''); setStatusFilter(''); setSearch(''); setServerQ('');
  }, []);

  // ── selection ─────────────────────────────────────────────────────────────
  const [sel, setSel] = useState<Set<number>>(new Set());
  const toggleSel = (id: number) =>
    setSel((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // ── upload ────────────────────────────────────────────────────────────────
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [outcomes, setOutcomes] = useState<UploadOutcome[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  // ── panel ─────────────────────────────────────────────────────────────────
  const [detail, setDetail] = useState<DocumentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAllText, setShowAllText] = useState(false);
  const [showText, setShowText] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [editSource, setEditSource] = useState<SourceType>('upload');
  const [editAuthor, setEditAuthor] = useState('');
  const [editFolder, setEditFolder] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [supersedeChoice, setSupersedeChoice] = useState('');
  const [pendingSupersede, setPendingSupersede] = useState<{ id: number; ref: string } | null>(null);
  const [dateEdit, setDateEdit] = useState<{ id: number; value: string } | null>(null);

  // ── holds: drawers ────────────────────────────────────────────────────────
  const [datesOpen, setDatesOpen] = useState(false);
  const [dateOff, setDateOff] = useState<Set<number>>(new Set());
  const [pairsOpen, setPairsOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // ── attachment re-scan ────────────────────────────────────────────────────
  const [rescanning, setRescanning] = useState(false);
  const [rescanReport, setRescanReport] = useState<RescanReport | null>(null);

  // ── library picker ────────────────────────────────────────────────────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRows, setPickerRows] = useState<LibraryDocumentRow[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerFolder, setPickerFolder] = useState('');
  const [pickerQ, setPickerQ] = useState('');
  const [linkingId, setLinkingId] = useState<number | null>(null);

  // ── disposals ─────────────────────────────────────────────────────────────
  const [pendingUnlink, setPendingUnlink] =
    useState<{ docId: number; ref: string; engagementId: number; engagementCode: string } | null>(null);
  const [unlinkRefusal, setUnlinkRefusal] =
    useState<{ ref: string; engagementCode: string; message: string; cited_by: Array<{ id: number; ref: string }> } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ ref: string; id: number; plan: DocumentDeletionPlan } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── note ──────────────────────────────────────────────────────────────────
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteDate, setNoteDate] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  // Engagement switch: the corpus, the selection and everything opened over it belong to ONE
  // engagement. Nothing survives the change.
  useEffect(() => {
    setSel(new Set()); setDetail(null); setOutcomes([]); setRescanReport(null);
    clearFilters();
  }, [engagementId, clearFilters]);

  // ── derived corpus facts ──────────────────────────────────────────────────

  const live = useMemo(() => docs.filter(isLive), [docs]);
  const undatedDocs = useMemo(() => live.filter((d) => !d.effective_date_confirmed), [live]);
  const unreadDocs = useMemo(
    () => live.filter((d) => d.meta?.no_text_layer && !ocrQueue.has(d.id)),
    [live, ocrQueue],
  );
  /** Everything unread OCR could actually read. A zip merely NAMED .pptx can never convert. */
  const ocrCandidates = useMemo(
    () => unreadDocs.filter((d) => !d.meta?.not_a_presentation && canOcrFile(d.original_filename)),
    [unreadDocs],
  );
  const darkDocs = useMemo(() => live.filter((d) => spansOf(d) === 0), [live]);
  const neverOpened = useMemo(() => live.filter((d) => d.ever_covered === false), [live]);
  const citedDocs = useMemo(() => live.filter((d) => spansOf(d) > 0), [live]);
  const spansTotal = useMemo(() => docs.reduce((n, d) => n + spansOf(d), 0), [docs]);
  const supersededCount = docs.length - live.length;

  /** Every OPEN pair, once — the union of (this.ref, candidate.ref) over both directions. */
  const pairs = useMemo<Pair[]>(() => {
    const byId = new Map(docs.map((d) => [d.id, d]));
    const seen = new Set<string>();
    const out: Pair[] = [];
    for (const d of docs) {
      const hint = d.meta?.near_duplicate_hint;
      if (!hint || d.near_duplicate_resolved) continue;
      for (const c of hint.candidates ?? []) {
        const key = [d.ref, c.ref].sort().join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ key, a: d, bRef: c.ref, bId: c.id, b: byId.get(c.id) ?? null, kind: hint.kind, reason: hint.reason });
      }
    }
    return out;
  }, [docs]);

  // ── the rail ──────────────────────────────────────────────────────────────

  const rail = useMemo<DocsRailCell[]>(() => {
    if (corpus.error && !corpus.lastOkAt) {
      return degradedRail(RAIL_KEYS, t('the corpus did not load')).map((c) => c as DocsRailCell);
    }
    const dated = live.length - undatedDocs.length;
    const read = live.length - unreadDocs.length;
    return [
      {
        key: 'CORPUS', value: String(docs.length), unit: tp(docs.length, 'doc', 'docs'),
        tone: docs.length ? 'ink' : 'nil', route: null,
        detail: supersededCount
          ? t('{live} live · {superseded} superseded', { live: live.length, superseded: supersededCount })
          : t('{live} live · none superseded', { live: live.length }),
      },
      {
        key: 'DATED', value: t('{a} of {b}', { a: dated, b: live.length }), unit: null,
        tone: undatedDocs.length ? 'attend' : 'ok', route: null,
        detail: undatedDocs.length
          ? tp(undatedDocs.length, '{n} effective date unconfirmed', '{n} effective dates unconfirmed')
          : t('every effective date confirmed'),
      },
      {
        key: 'READ', value: t('{a} of {b}', { a: read, b: live.length }), unit: null,
        tone: unreadDocs.length ? 'attend' : 'ok', route: null,
        detail: unreadDocs.length ? t('{n} awaiting OCR', { n: unreadDocs.length }) : t('every document has a text layer'),
      },
      {
        key: 'PAIRS', value: pairs.length ? String(pairs.length) : '—', unit: pairs.length ? t('open') : null,
        tone: pairs.length ? 'attend' : 'nil', route: null,
        detail: pairs.length ? t('near-duplicates undecided') : t('every near-duplicate decided'),
      },
      {
        // Lean UX p.34: "28 documents ingested" is an OUTPUT. This is the outcome.
        key: 'YIELD', value: String(spansTotal), unit: tp(spansTotal, 'span', 'spans'),
        tone: spansTotal ? 'ink' : 'nil', route: null,
        detail: darkDocs.length
          ? t('{cited} of {live} cited · {dark} dark', { cited: citedDocs.length, live: live.length, dark: darkDocs.length })
          : t('every document cited'),
      },
    ];
  }, [corpus.error, corpus.lastOkAt, docs.length, live.length, supersededCount,
    undatedDocs.length, unreadDocs.length, pairs.length, spansTotal, darkDocs.length, citedDocs.length, t, tp]);

  // ── mutations ─────────────────────────────────────────────────────────────

  const openDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    setShowAllText(false); setShowText(false); setEditing(false);
    setRescanReport((r) => (r && r.document_id !== id ? null : r));
    try {
      const d = await documentsApi.get(id, engagementId ?? undefined);
      setDetail(d);
      setEditDate(d.effective_date ?? '');
      setEditSource(d.source_type);
      setEditAuthor(d.authored_by ?? '');
      setEditFolder(d.folder ?? '');
      setEditTags((d.tags ?? []).join(', '));
      setEditSummary(d.ai_summary ?? '');
      setSupersedeChoice(d.superseded_by_document_id ? String(d.superseded_by_document_id) : '');
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Failed to load document'));
    } finally {
      setDetailLoading(false);
    }
  }, [engagementId, toast, t]);

  const ingest = useCallback(async (files: File[]) => {
    if (!engagementId || !files.length) return;
    setUploading(true);
    try {
      const report = await documentsApi.uploadFiles(engagementId, files);
      setOutcomes(report.results);
      const created = report.results.filter((r) => r.status === 'created').length;
      const duplicates = report.results.filter((r) => r.status === 'duplicate').length;
      const failed = report.results.filter((r) => r.status === 'failed').length;
      const summary = [
        t('{n} ingested', { n: created }),
        duplicates ? t('{n} already on file', { n: duplicates }) : '',
        failed ? t('{n} not accepted', { n: failed }) : '',
      ].filter(Boolean).join(' · ');
      if (!report.results.length) toast('warn', t('Nothing was ingested'));
      else toast(failed ? 'warn' : 'ok', t('{summary} — the per-file report is the first hold', { summary }));
    } catch (err) {
      // Every chosen file still gets a row: the operator must never be left guessing which of
      // the twenty landed.
      const reason = err instanceof Error ? err.message : t('Upload failed');
      setOutcomes(files.map((f) => ({ filename: f.name, status: 'failed' as const, error: reason })));
      toast('err', t('Upload failed — {reason}', { reason }));
    } finally {
      setUploading(false);
      void refresh();
    }
  }, [engagementId, refresh, toast, t]);

  const confirmDate = async (id: number) => {
    try {
      await documentsApi.confirmDate(id);
      toast('ok', t('Effective date confirmed'));
      await refresh();
      if (detail?.id === id) await openDetail(id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not confirm date'));
    }
  };

  /** The headline micro-sequence: 25 clicks and 25 refetches become 1 and 1. */
  const confirmDates = useCallback(async (ids: number[]) => {
    if (!engagementId) return;
    if (!ids.length) { toast('warn', t('None of the selected documents has a detected date waiting to be confirmed')); return; }
    setConfirming(true);
    try {
      const out = await documentsApi.confirmDates(engagementId, ids);
      if (out.refused.length) {
        toast('warn', t('{done} confirmed · {refused} refused — {reason}', {
          done: tp(out.done.length, '{n} date', '{n} dates'), refused: out.refused.length, reason: out.refused[0].reason,
        }));
      } else {
        toast('ok', tp(out.done.length, '{n} effective date confirmed in one act', '{n} effective dates confirmed in one act'));
      }
      setDatesOpen(false);
      await refresh();
      if (detail && ids.includes(detail.id)) await openDetail(detail.id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not confirm the dates'));
    } finally {
      setConfirming(false);
    }
  }, [engagementId, refresh, toast, detail, openDetail, t, tp]);

  /** Row-level "set date": write the date and confirm it in one act. */
  const saveDateInline = async (id: number, value: string) => {
    try {
      await documentsApi.update(id, { effective_date: value });
      await documentsApi.confirmDate(id);
      setDateEdit(null);
      toast('ok', t('Date set and confirmed'));
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not set the date'));
    }
  };

  const runOcr = useCallback(async (id: number) => {
    try {
      const r = await documentsApi.runOcr(id);
      setOcrQueue((prev) => new Set(prev).add(id));
      toast('ok', r.replacing ? t('Re-reading — the current text will be replaced') : t('Queued for reading'));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not start OCR'));
    }
  }, [toast, t]);

  const runOcrMany = useCallback(async (ids: number[]) => {
    // A bulk verb that quietly does nothing is worse than one that refuses out loud.
    if (!ids.length) { toast('warn', t('None of the selected documents is a format the vision model can read')); return; }
    setOcrQueue((prev) => { const n = new Set(prev); ids.forEach((i) => n.add(i)); return n; });
    let failed = 0;
    for (const id of ids) {
      try { await documentsApi.runOcr(id); } catch { failed++; }
    }
    toast(failed ? 'warn' : 'ok',
      failed ? t('Queued {done} of {total} — {failed} could not start', { done: ids.length - failed, total: ids.length, failed })
        : tp(ids.length, 'Queued {n} document for reading', 'Queued {n} documents for reading'));
  }, [toast, t, tp]);

  const reclassify = useCallback(async (id: number) => {
    try {
      const r = await documentsApi.classify(id);
      setLibQueue((prev) => new Set(prev).add(id));
      toast('ok', r.replacing
        ? t('Queued — the librarian will replace the current classification')
        : t('Queued for classification'));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not start the librarian'));
    }
  }, [toast, t]);

  const reclassifyMany = useCallback(async (ids: number[]) => {
    if (!ids.length) { toast('warn', t('None of the selected documents has text for the librarian to read')); return; }
    setLibQueue((prev) => { const n = new Set(prev); ids.forEach((i) => n.add(i)); return n; });
    let failed = 0;
    for (const id of ids) {
      try { await documentsApi.classify(id); } catch { failed++; }
    }
    toast(failed ? 'warn' : 'ok',
      failed ? t('Queued {done} of {total} for re-filing — {failed} could not start', { done: ids.length - failed, total: ids.length, failed })
        : tp(ids.length, 'Queued {n} document for re-filing', 'Queued {n} documents for re-filing'));
  }, [toast, t, tp]);

  /** ATTEST LAW 6: ◇ becomes ▪ — the operator is standing behind the librarian's filing. */
  const holdClassification = async (id: number) => {
    try {
      await documentsApi.update(id, { classification_held: true });
      toast('ok', t('Classification held — it is yours now, not the librarian’s'));
      await refresh();
      if (detail?.id === id) await openDetail(id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not hold the classification'));
    }
  };

  const saveMetadata = async () => {
    if (!detail) return;
    try {
      await documentsApi.update(detail.id, {
        effective_date: editDate || null,
        source_type: editSource,
        authored_by: editAuthor || null,
        folder: (editFolder || null) as DocumentFolder | null,
        tags: editTags.split(',').map((t) => t.trim()).filter(Boolean),
        ai_summary: editSummary.trim() || null,
      });
      toast('ok', t('{ref} updated', { ref: detail.ref }));
      setEditing(false);
      await refresh();
      await openDetail(detail.id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Update failed'));
    }
  };

  const applySupersede = async (id: number, successorId: number | null) => {
    try {
      await documentsApi.supersede(id, successorId);
      toast('ok', successorId ? t('Marked as superseded') : t('Supersession cleared'));
      await refresh();
      if (detail) await openDetail(detail.id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Supersession failed'));
    } finally {
      setPendingSupersede(null);
    }
  };

  /** The pair decider's verbs: either direction is a peer, and neither is pre-applied (P1). */
  const decidePair = async (loserId: number, winnerId: number) => {
    try {
      await documentsApi.supersede(loserId, winnerId);
      toast('ok', t('Supersession recorded — recency arbitration now has an order'));
      await refresh();
      if (detail) await openDetail(detail.id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Supersession failed'));
    }
  };

  const dismissNearDuplicate = async (id: number) => {
    try {
      await documentsApi.dismissNearDuplicate(id);
      toast('ok', t('Recorded as reviewed — the hint keeps its reason, the badge stands down'));
      await refresh();
      if (detail?.id === id) await openDetail(id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not dismiss the hint'));
    }
  };

  const rescanAttachments = useCallback(async (id: number) => {
    setRescanning(true);
    try {
      const report = await documentsApi.rescanAttachments(id);
      const parts = [
        report.created ? t('{n} ingested', { n: report.created }) : '',
        report.linked_existing ? t('{n} linked to existing', { n: report.linked_existing }) : '',
        report.already_present ? t('{n} already present', { n: report.already_present }) : '',
        report.skipped_attachments.length ? t('{n} still unsupported', { n: report.skipped_attachments.length }) : '',
      ].filter(Boolean).join(' · ');
      toast(report.created || report.linked_existing ? 'ok' : 'warn',
        parts ? t('Re-scan of {ref}: {parts}', { ref: report.ref, parts }) : t('Re-scan of {ref}: no attachments found', { ref: report.ref }));
      await refresh();
      await openDetail(id);
      setRescanReport(report);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Attachment re-scan failed'));
    } finally {
      setRescanning(false);
    }
  }, [refresh, openDetail, toast, t]);

  const askDelete = useCallback(async (id: number, ref: string) => {
    try {
      setDeleteTarget({ id, ref, plan: await documentsApi.deletionPlan(id) });
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not work out what deleting this would do'));
    }
  }, [toast, t]);

  const runDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const out = await documentsApi.remove(deleteTarget.id);
      toast('ok', out.file_removed
        ? t('{ref} deleted — the stored file went with it', { ref: out.ref })
        : t('{ref} deleted', { ref: out.ref }));
      if (detail?.id === deleteTarget.id) setDetail(null);
      setSel((prev) => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  const runUnlink = async () => {
    if (!pendingUnlink) return;
    const { docId, ref, engagementId: engId, engagementCode } = pendingUnlink;
    setPendingUnlink(null);
    try {
      await documentsApi.unlink(docId, engId);
      toast('ok', t('{ref} unlinked from {code} — it stays in the client library', { ref, code: engagementCode }));
      await refresh();
      if (detail?.id === docId) {
        if (engId === engagementId) setDetail(null);
        else await openDetail(docId);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setUnlinkRefusal({
          ref, engagementCode, message: err.message,
          cited_by: Array.isArray(err.body.cited_by) ? err.body.cited_by as Array<{ id: number; ref: string }> : [],
        });
      } else {
        toast('err', err instanceof Error ? err.message : t('Could not unlink the document'));
      }
    }
  };

  /** Bulk unlink surfaces each 409 with its own reason rather than aborting the batch. */
  const unlinkMany = useCallback(async (ids: number[]) => {
    if (!engagementId || !ids.length) return;
    const refused: string[] = [];
    let done = 0;
    for (const id of ids) {
      try { await documentsApi.unlink(id, engagementId); done++; } catch (err) {
        const row = docs.find((d) => d.id === id);
        refused.push(`${row?.ref ?? id}: ${err instanceof Error ? err.message : t('refused')}`);
      }
    }
    toast(refused.length ? 'warn' : 'ok',
      refused.length
        ? t('{done} unlinked · {refused} refused — {first}', { done, refused: refused.length, first: refused[0] })
        : tp(done, '{n} document unlinked from {code}', '{n} documents unlinked from {code}', { code: current?.code ?? t('this engagement') }));
    setSel(new Set());
    await refresh();
  }, [engagementId, docs, current, refresh, toast, t, tp]);

  const saveNote = async () => {
    if (!engagementId) return;
    setNoteSaving(true);
    try {
      const doc = await documentsApi.createNote(engagementId, noteTitle, noteBody, noteDate || null);
      toast('ok', t('{ref} note created', { ref: doc.ref }));
      setNoteOpen(false); setNoteTitle(''); setNoteBody(''); setNoteDate('');
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not save note'));
    } finally {
      setNoteSaving(false);
    }
  };

  const loadPicker = useCallback(async () => {
    if (!current || !engagementId) return;
    setPickerLoading(true);
    try {
      const res = await documentsApi.library(current.client_id, {
        engagementId, linked: false,
        folder: pickerFolder || undefined,
        q: pickerQ.trim() || undefined,
      });
      setPickerRows(res.rows);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not load the client library'));
    } finally {
      setPickerLoading(false);
    }
  }, [current, engagementId, pickerFolder, pickerQ, toast, t]);

  useEffect(() => { if (pickerOpen) void loadPicker(); }, [pickerOpen, loadPicker]);

  const linkFromLibrary = async (doc: LibraryDocumentRow) => {
    if (!engagementId) return;
    setLinkingId(doc.id);
    try {
      const out = await documentsApi.link(doc.id, engagementId);
      toast('ok', t('{ref} linked into {code} — it stays in the client library', { ref: doc.ref, code: out.engagement_code }));
      setPickerRows((prev) => prev.filter((r) => r.id !== doc.id));
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not link the document'));
    } finally {
      setLinkingId(null);
    }
  };

  // ── the visible set ───────────────────────────────────────────────────────

  const hitName = useCallback((d: DocumentRow, q: string) =>
    `${d.ref} ${d.original_filename}`.toLowerCase().includes(q), []);

  const visible = useMemo(() => {
    const q = serverQ.toLowerCase();
    const rows = docs.filter((d) => {
      if (folder && (d.folder ?? 'unfiled') !== folder) return false;
      if (statusFilter && d.status !== statusFilter) return false;
      if (railFilter === 'undated' && (d.effective_date_confirmed || !isLive(d))) return false;
      if (railFilter === 'unread' && !(d.meta?.no_text_layer && isLive(d))) return false;
      if (railFilter === 'pairs' && !inPair(d)) return false;
      if (railFilter === 'dark' && !(spansOf(d) === 0 && isLive(d))) return false;
      // While the text search is in flight the client-side half is already honest; the server's
      // answer only ever ADDS rows (it sees extracted_text, which the client never does).
      if (q) return textHits ? textHits.has(d.id) : hitName(d, q);
      return true;
    });
    const sorted = [...rows];
    if (sortBy === 'yield') sorted.sort((a, b) => spansOf(b) - spansOf(a) || (b.effective_date ?? '').localeCompare(a.effective_date ?? ''));
    if (sortBy === 'ingest') sorted.sort((a, b) => (b.ingested_at ?? '').localeCompare(a.ingested_at ?? ''));
    return sorted;
  }, [docs, folder, statusFilter, railFilter, serverQ, textHits, hitName, sortBy]);

  const filtered = !!(folder || railFilter || statusFilter || serverQ);
  const maxSpans = useMemo(() => Math.max(1, ...docs.map(spansOf)), [docs]);

  const folderCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of docs) {
      const key = d.folder ?? 'unfiled';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [docs]);
  const absentFolders = DOCUMENT_FOLDERS.filter((f) => !folderCounts.get(f));

  const nameHits = useMemo(
    () => (serverQ ? docs.filter((d) => hitName(d, serverQ.toLowerCase())).length : 0),
    [docs, serverQ, hitName],
  );

  // ── HOLDS ─────────────────────────────────────────────────────────────────
  // One card per KIND of hold, ranked, each with ONE consequence sentence and ONE bulk verb.
  // A count that names a job must also offer the job.

  const holds = useMemo<Hold[]>(() => {
    const out: Hold[] = [];

    if (outcomes.length) {
      const created = outcomes.filter((o) => o.status === 'created').length;
      const dup = outcomes.filter((o) => o.status === 'duplicate').length;
      const failed = outcomes.filter((o) => o.status === 'failed').length;
      out.push({
        rule: 'ingest-report',
        weight: 10,
        tone: failed ? 'attend' : 'quiet',
        title: <>{t('Last ingest —')} <span className="voice-figure">{outcomes.length}</span> {tp(outcomes.length, 'file', 'files')}</>,
        why: (
          <>
            <span className="voice-figure">{created}</span> {t('ingested')}
            {dup ? <> · <span className="voice-figure">{dup}</span> {t('already on file')}</> : null}
            {failed ? <> · <span className="voice-figure">{failed}</span> {t('not accepted')}</> : null}
            {failed ? ` ${t('— a file that never became a document is a hold, not a toast.')}` : '.'}
          </>
        ),
        cta: <button className="btn btn-sm btn-ghost" onClick={() => setOutcomes([])}><X className="icon-sm" /> {t('Dismiss')}</button>,
        drawer: (
          <table className="table table-compact">
            <tbody>
              {outcomes.map((o, i) => (
                <tr key={`${o.filename}-${i}`}>
                  <td style={{ width: 96 }}>
                    {o.status === 'created' && <span className="docs-tag docs-tag--ok">{t('created')}</span>}
                    {o.status === 'duplicate' && (o.linked
                      ? <span className="docs-tag" title={t('The client library already had these bytes — the existing document was linked in')}>{t('linked')}</span>
                      : <span className="docs-tag docs-tag--attend">{t('duplicate')}</span>)}
                    {o.status === 'failed' && <span className="docs-tag docs-tag--broken">{t('failed')}</span>}
                  </td>
                  <td style={{ width: 86 }} className="src-ref">{o.document?.ref ?? o.existing_ref ?? '—'}</td>
                  <td>
                    <span className="text-ink-1">{o.filename}</span>
                    {o.notice && <div className="text-xs text-ink-3">{o.notice}</div>}
                    {o.error && <div className="text-xs" style={{ color: 'var(--err)' }}>{o.error}</div>}
                    {o.hint && <div className="text-xs text-ink-3">⇒ {o.hint.reason}</div>}
                    {o.document?.meta?.no_text_reason && <div className="text-xs text-ink-3">⚑ {o.document.meta.no_text_reason}</div>}
                  </td>
                  <td style={{ width: 116, textAlign: 'right' }}>
                    {o.status === 'duplicate' && o.duplicate_count
                      ? <span className="docs-tag" title={t('Recorded on the existing document')}>{t('re-uploaded')} <span className="voice-figure">{o.duplicate_count}</span>×</span>
                      : null}
                  </td>
                  <td style={{ width: 90, textAlign: 'right' }}>
                    {(o.document?.id ?? o.existing_document_id) && (
                      <button className="btn btn-sm" onClick={() => void openDetail((o.document?.id ?? o.existing_document_id)!)}>{t('Open')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ),
      });
    }

    if (undatedDocs.length) {
      const on = undatedDocs.filter((d) => !dateOff.has(d.id) && d.effective_date);
      const undetected = undatedDocs.filter((d) => !d.effective_date).length;
      out.push({
        rule: 'dates-unconfirmed',
        weight: 20,
        title: <><span className="voice-figure">{undatedDocs.length}</span> {tp(undatedDocs.length, 'effective date unconfirmed', 'effective dates unconfirmed')}</>,
        why: <>{t('Until you confirm them, recency arbitration is running on Signet’s guess.')}{undetected ? ` ${t('{n} of them have no detected date at all — set those by hand in the grid.', { n: undetected })}` : ''}</>,
        cta: (
          <>
            <button className="btn btn-sm btn-ghost" onClick={() => setDatesOpen((v) => !v)}>
              {datesOpen ? t('Hide the list') : t('Check the list')}
            </button>
            <button
              className="btn"
              disabled={!on.length || confirming}
              onClick={() => void confirmDates(on.map((d) => d.id))}
            >{confirming ? t('Confirming…') : t('Confirm {n}', { n: on.length })}</button>
          </>
        ),
        drawer: datesOpen ? (
          <>
            <table className="docs-dl">
              <tbody>
                {undatedDocs.map((d) => (
                  <tr key={d.id} className={dateOff.has(d.id) ? 'docs-off' : ''}>
                    <td style={{ width: 26 }}>
                      <input
                        type="checkbox"
                        checked={!dateOff.has(d.id) && !!d.effective_date}
                        disabled={!d.effective_date}
                        onChange={(e) => setDateOff((prev) => {
                          const n = new Set(prev);
                          if (e.target.checked) n.delete(d.id); else n.add(d.id);
                          return n;
                        })}
                      />
                    </td>
                    <td className="src-ref" style={{ width: 82 }}>{d.ref}</td>
                    <td className="voice-figure" style={{ width: 104 }}>
                      {d.effective_date ? fmtDate(d.effective_date) : <span className="n-none">—</span>}
                    </td>
                    <td className="docs-c-src" style={{ width: 250 }}>
                      {d.effective_date
                        ? <>{t('detected from {source}', { source: d.meta?.effective_date_detected_from ?? t('source') })}
                          {d.meta?.effective_date_pattern ? <> (<span className="src-ref">{d.meta.effective_date_pattern}</span>)</> : null}</>
                        : t('no date found — set it by hand')}
                    </td>
                    <td className="docs-c-file" title={d.original_filename}>{d.original_filename}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="docs-note">
              {t('Unticking a row leaves it unconfirmed and in the count; correcting a date is one click on the date cell in the grid.')}
            </div>
          </>
        ) : undefined,
      });
    }

    if (pairs.length) {
      out.push({
        rule: 'pairs-open',
        weight: 30,
        title: <><span className="voice-figure">{pairs.length}</span> {tp(pairs.length, 'near-duplicate pair undecided', 'near-duplicate pairs undecided')}</>,
        why: <>{t('While a pair is open, recency arbitration has no order and the agent may cite the stale half of it.')}</>,
        cta: <button className="btn" onClick={() => setPairsOpen((v) => !v)}>{pairsOpen ? t('Close') : t('Decide pairs')}</button>,
        drawer: pairsOpen ? (
          <div>{pairs.map((p) => <PairCard key={p.key} pair={p} onSupersede={decidePair} onDismiss={dismissNearDuplicate} />)}</div>
        ) : undefined,
      });
    }

    if (unreadDocs.length) {
      out.push({
        rule: 'text-missing',
        weight: 40,
        title: <><span className="voice-figure">{unreadDocs.length}</span> {tp(unreadDocs.length, 'document with no text layer', 'documents with no text layer')}</>,
        why: <>{t('Stored and flagged, never dropped — but nothing can cite a document Signet cannot read.')}</>,
        cta: (
          <button
            className="btn"
            disabled={!ocrCandidates.length || ocrUp?.available === false}
            title={ocrUp?.available === false ? ocrUp.reason ?? t('The vision model is unreachable') : undefined}
            onClick={() => void runOcrMany(ocrCandidates.map((d) => d.id))}
          >
            <ScanText className="icon-sm" /> {t('Read {n} with OCR', { n: ocrCandidates.length })}
          </button>
        ),
      });
    }

    if (neverOpened.length) {
      out.push({
        rule: 'never-covered',
        weight: 50,
        tone: 'quiet',
        title: <><span className="voice-figure">{neverOpened.length}</span> {tp(neverOpened.length, 'document the agent has never opened', 'documents the agent has never opened')}</>,
        why: <>{t('Never in any run’s')} <span className="src-ref">docs_covered</span> — <strong>{t('G1 cannot close on a corpus nobody has read.')}</strong></>,
        cta: (
          <>
            <button className="btn btn-sm btn-ghost" onClick={() => { setRailFilter('dark'); }}>{t('Show them')}</button>
            <button
              className="btn"
              onClick={() => void openRoute(ROUTES.agent({ engagement: engagementId, docs: neverOpened.map((d) => d.id) }))}
            >{t('Queue for extraction')}</button>
          </>
        ),
      });
    }

    return out.sort((a, b) => a.weight - b.weight || a.rule.localeCompare(b.rule));
  }, [outcomes, undatedDocs, dateOff, datesOpen, confirming, confirmDates, pairs, pairsOpen,
    unreadDocs, ocrCandidates, ocrUp, runOcrMany, neverOpened, openRoute, engagementId, openDetail, t, tp, fmtDate]);

  // ── the page ──────────────────────────────────────────────────────────────

  if (!current) {
    return (
      <div className="page docs-page">
        <header className="mast"><div className="mast-top"><div className="mast-main">
          <h1>{t('Documents')}</h1>
        </div></div></header>
        <EmptyState
          kind="empty"
          title={t('No engagement selected')}
          detail={t('Evidence is always scoped to one engagement — pick one in the top bar and its corpus loads here.')}
        />
      </div>
    );
  }

  const previewText = detail?.extracted_text ?? '';
  const truncated = previewText.length > PREVIEW_CHARS && !showAllText;
  const selected = visible.filter((d) => sel.has(d.id));
  const allShownSelected = visible.length > 0 && visible.every((d) => sel.has(d.id));

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    void ingest(Array.from(e.dataTransfer.files));
  };

  return (
    <div
      className="page docs-page"
      onDragEnter={(e) => { if (e.dataTransfer.types.includes('Files')) { dragDepth.current++; setDragging(true); } }}
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
      onDragLeave={() => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); }}
      onDrop={onDrop}
    >
      {/* The whole page is the drop target; the zone appears only while a drag is over it.
          The old permanent 110px zone carried two lines of pure instruction (Krug p.78) that
          the ingest report already states at the moment they matter. */}
      {dragging && (
        <div className="docs-droplay">
          <div className="docs-dropcard">{t('Release to ingest into')} <span className="src-ref">{current.code}</span></div>
        </div>
      )}

      {/* ── Zone 1 · masthead ─────────────────────────────────────────────── */}
      <header className="mast">
        <div className="mast-top">
        <div className="mast-main">
          <h1>{t('Documents')}</h1>
          <p className="mast-sub">
            {t('The evidence corpus of')} <span className="src-ref">{current.code}</span> · {t('everything a requirement is allowed to cite')}
            {corpus.notice ? <> · <span className="text-ink-3">{corpus.notice}</span></> : null}
          </p>
        </div>
        <div className="mast-verbs">
          <button className="btn" title={t('Link documents the client library already has — nothing is copied')} onClick={() => setPickerOpen(true)}>
            <Library className="icon-sm" /> {t('Add from library')}
          </button>
          <button className="btn" onClick={() => setNoteOpen(true)}>
            <FilePlus2 className="icon-sm" /> {t('New note')}
          </button>
          <button className="btn btn-accent" disabled={uploading} onClick={() => fileInput.current?.click()}>
            <Upload className="icon-sm" /> {uploading ? t('Ingesting…') : t('Upload files')}
          </button>
        </div>
        </div>
      </header>
      <input
        ref={fileInput} type="file" multiple accept={ACCEPT} style={{ display: 'none' }}
        onChange={(e) => { const files = Array.from(e.target.files ?? []); e.target.value = ''; void ingest(files); }}
      />

      {/* ── Zone 2 · the instrument rail ──────────────────────────────────── */}
      <div className="instr">
        <div className="rail">
          {rail.map((cell) => {
            const filter = RAIL_FILTER[cell.key];
            const pressed = !!filter && railFilter === filter;
            return (
              <button
                key={cell.key}
                type="button"
                className="fld"
                aria-pressed={pressed}
                disabled={!filter || cell.tone === 'unknown'}
                onClick={() => setRailFilter(pressed ? '' : filter)}
              >
                <div className="fl u-label">
                  {t(cell.key)}
                  {filter && cell.tone !== 'unknown' && <span className="go" aria-hidden="true">{t('filter')}</span>}
                </div>
                <div className={`fv voice-figure${TONE_CLASS[cell.tone] ?? ''}`}>
                  {cell.tone === 'unknown' ? <span className="n-unknown" title={cell.detail}>—</span> : cell.value}
                  {cell.unit && <span className="u">{cell.unit}</span>}
                </div>
                <div className="fd">{cell.detail}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Zone 3 · HOLDS ────────────────────────────────────────────────── */}
      <section className="zone">
        <div className="zone-head">
          <span className="u-label">{t('Holds')}</span>
          <span className="zone-note">
            {holds.length ? tp(holds.length, '{n} kind of hold', '{n} kinds of hold') : tCtx(t, 'clear', 'holds')}
          </span>
        </div>

        {corpus.error && !corpus.lastOkAt ? (
          <EmptyState
            kind="error"
            title={t('The corpus did not load')}
            detail={corpus.error}
            onRetry={() => void refresh()}
          />
        ) : holds.length === 0 ? (
          <div className="docs-hold docs-hold--done">
            <div className="docs-hold-head">
              <div className="docs-hold-mark" />
              <div className="docs-hold-body">
                <div className="docs-hold-t">{t('Nothing is holding the corpus')}</div>
                <div className="docs-hold-w">
                  {t('Every effective date confirmed, every near-duplicate decided, every readable file read. The corpus is ready for extraction.')}
                </div>
              </div>
            </div>
          </div>
        ) : (
          holds.map((h) => (
            <div key={h.rule} className={`docs-hold${h.tone === 'quiet' ? ' docs-hold--quiet' : ''}`}>
              <div className="docs-hold-head">
                <div className="docs-hold-mark" />
                <div className="docs-hold-body">
                  <div className="docs-hold-t">{h.title}</div>
                  <div className="docs-hold-w">{h.why}</div>
                </div>
                <div className="docs-hold-cta">{h.cta}</div>
              </div>
              {h.drawer && <div className="docs-hold-drawer">{h.drawer}</div>}
            </div>
          ))
        )}
      </section>

      {/* ── Zone 4 · CORPUS ───────────────────────────────────────────────── */}
      <section className="zone">
        <div className="zone-head">
          <span className="u-label">{t('Corpus')}</span>
          <span className="zone-note">
            {filtered ? t('{shown} of {total} documents', { shown: visible.length, total: docs.length }) : tp(docs.length, '{n} document', '{n} documents')}
          </span>
        </div>

        {/* THE OPERATOR'S DESIGN LAW: the controls live INSIDE the sheet they act on. The
            toolbar, the search report and the selection bar are the head of the corpus
            container, not a strip floating on the field above it. */}
        {corpus.loading && !docs.length ? (
          <div className="empty-state-card"><div className="empty-state"><div className="spinner" /></div></div>
        ) : corpus.error && !corpus.lastOkAt ? (
          <EmptyState kind="error" title={t('The corpus did not load')} detail={corpus.error} onRetry={() => void refresh()} />
        ) : docs.length === 0 ? (
          <EmptyState
            kind="empty"
            title={t('No evidence ingested yet')}
            detail={<>{t('Nothing is linked to')} <span className="src-ref">{current.code}</span>. {t('Drop the discovery corpus onto this page — transcripts, client specs, screenshots — or link what the client library already has.')}</>}
          >
            <button className="btn btn-sm" onClick={() => fileInput.current?.click()}>{t('Upload files')}</button>
            <button className="btn btn-sm" onClick={() => setPickerOpen(true)}>{t('Add from library')}</button>
          </EmptyState>
        ) : (
          <div className="sheet sheet--clip docs-gridwrap">
        <div className="docs-toolbar">
          {/* FOLDER is what the document IS; source_type was how the bytes ARRIVED — two
              vocabularies, and the old chips filtered on the wrong one (Smashing p.282).
              Every facet carries its count, so a filter can never silently return nothing. */}
          <div className="filter-chips">
            <button className={`chip${folder === '' ? ' active' : ''}`} onClick={() => setFolder('')}>
              {t('All')} <span className="docs-chip-n voice-figure">{docs.length}</span>
            </button>
            {[...DOCUMENT_FOLDERS, 'unfiled'].filter((f) => folderCounts.get(f)).map((f) => (
              <button
                key={f}
                className={`chip${folder === f ? ' active' : ''}`}
                onClick={() => setFolder(folder === f ? '' : f)}
              >
                {t(f)} <span className="docs-chip-n voice-figure">{folderCounts.get(f)}</span>
              </button>
            ))}
            {absentFolders.length > 0 && (
              <span className="docs-chipnote">{t('none filed as {folders}', { folders: absentFolders.map((f) => t(f)).join(', ') })}</span>
            )}
          </div>
          <select
            className="form-select" style={{ width: 'auto' }} title={t('Sorting')}
            value={sortBy} onChange={(e) => setSortBy(e.target.value as 'date' | 'yield' | 'ingest')}
          >
            <option value="date">{t('by effective date, newest first')}</option>
            <option value="yield">{t('by yield, richest first')}</option>
            <option value="ingest">{t('by ingest, newest first')}</option>
          </select>
          <div className="search-wrapper docs-search">
            <input
              type="text" className="search-input"
              placeholder={t('Search refs, names, summaries, tags, full text…')}
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
            {search && <button className="search-clear" onClick={() => setSearch('')}>×</button>}
          </div>
          {/* Never show a control with nothing to do. */}
          {filtered && <button className="zone-route docs-zroute" onClick={clearFilters}>{t('clear filters')}</button>}
        </div>

        {statusFilter && (
          <div className="docs-searchnote">
            {t('Status filter:')} <span className="src-ref">{statusFilter}</span>{' '}
            <button className="zone-route docs-zroute" style={{ marginLeft: 6 }} onClick={() => setStatusFilter('')}>{tCtx(t, 'clear', 'filter')}</button>
          </div>
        )}

        {serverQ && (
          <div className="docs-searchnote">
            {searchError
              ? <>{t('The text search failed: {error} — showing only refs and filenames.', { error: searchError })}</>
              : searching
                ? t('Searching the corpus text…')
                : visible.length
                  ? <>
                    <span className="voice-figure">{nameHits}</span> {t('in refs and filenames')} ·{' '}
                    <span className="voice-figure">{Math.max(0, visible.length - nameHits)}</span>{' '}
                    {t('only in summaries, tags or full text')}
                  </>
                  : null}
          </div>
        )}

        {sel.size > 0 && (
          <div className="docs-selbar">
            <span><span className="voice-figure" style={{ fontWeight: 600 }}>{sel.size}</span> {t('selected')}</span>
            <button
              className="btn btn-sm"
              onClick={() => void confirmDates(selected.filter((d) => d.effective_date && !d.effective_date_confirmed).map((d) => d.id))}
            >{t('Confirm dates')}</button>
            <button
              className="btn btn-sm"
              disabled={ocrUp?.available === false}
              onClick={() => void runOcrMany(selected.filter((d) => canOcrFile(d.original_filename)).map((d) => d.id))}
            >{t('Read with OCR')}</button>
            <button className="btn btn-sm" onClick={() => void reclassifyMany(selected.filter((d) => d.has_text).map((d) => d.id))}>
              {t('Re-file with the librarian')}
            </button>
            <button className="btn btn-sm" onClick={() => void unlinkMany(selected.map((d) => d.id))}>
              {t('Unlink from {code}', { code: current.code })}
            </button>
            <button className="btn btn-sm btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => setSel(new Set())}>{t('Clear')}</button>
          </div>
        )}

        {visible.length === 0 ? (
          <div className="docs-empty-in">
            <div className="empty-state-title">
              {serverQ ? t('Nothing matches “{q}”', { q: serverQ }) : t('Nothing here under these filters')}
            </div>
            <div className="empty-state-desc">
              {serverQ
                ? t('The corpus search reads refs, filenames, summaries, tags and the extracted text of every document — try a phrase you remember reading.')
                : tp(docs.length, 'The corpus has {n} document.', 'The corpus has {n} documents.')}
            </div>
            <button className="btn btn-sm" style={{ marginTop: 'var(--s16)' }} onClick={clearFilters}>{t('Clear the filters')}</button>
          </div>
        ) : (
            <table className="tbl docs-grid">
              <thead>
                <tr>
                  <th className="docs-c-sel">
                    <input
                      type="checkbox" title={t('Select all shown')} checked={allShownSelected}
                      onChange={(e) => setSel(e.target.checked ? new Set(visible.map((d) => d.id)) : new Set())}
                    />
                  </th>
                  <th className="docs-c-date">{t('Effective')}</th>
                  <th className="docs-c-ref">{t('Ref')}</th>
                  <th>{t('Document')}</th>
                  <th className="docs-c-yield">{t('Yield')}</th>
                  <th className="docs-c-hold">{tCtx(t, 'Hold', 'corpus')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((d, i) => {
                  // The corpus is a chronology (DESIGN §7) — but only while the chronology is
                  // the sort. 28 flat rows is over the working-memory buffer (Laws of UX p.31).
                  const group = sortBy === 'date' ? monthOf(d) : null;
                  const newGroup = group !== null && (i === 0 || monthOf(visible[i - 1]) !== group);
                  return (
                    <DocRow
                      key={d.id}
                      d={d}
                      group={newGroup ? group : null}
                      groupCount={newGroup ? visible.filter((r) => monthOf(r) === group).length : 0}
                      maxSpans={maxSpans}
                      selected={sel.has(d.id)}
                      ocrQueued={ocrQueue.has(d.id)}
                      libQueued={libQueue.has(d.id)}
                      dateEdit={dateEdit?.id === d.id ? dateEdit.value : null}
                      onToggleSel={() => toggleSel(d.id)}
                      onOpen={() => void openDetail(d.id)}
                      onStartDate={() => setDateEdit({ id: d.id, value: '' })}
                      onChangeDate={(v) => setDateEdit({ id: d.id, value: v })}
                      onSaveDate={(v) => void saveDateInline(d.id, v)}
                      onCancelDate={() => setDateEdit(null)}
                    />
                  );
                })}
              </tbody>
            </table>
        )}
          </div>
        )}
      </section>

      {/* ── Zone 5 · the panel ────────────────────────────────────────────── */}
      <div className={`panel-overlay${detail ? ' open' : ''}`}>
        <div className="panel-overlay-bg" onClick={() => setDetail(null)} />
        <div className="panel-slide" style={{ width: 'min(660px, 94vw)' }}>
          {detail && (
            <>
              <div className="panel-slide-head">
                <div style={{ minWidth: 0 }}>
                  <div className="text-xs text-ink-3 flex gap-8 flex-wrap items-baseline">
                    <span className="src-ref">{detail.ref}</span>
                    <span>{current.client_name ?? t('client #{id}', { id: detail.client_id ?? '—' })}</span>
                    <span>
                      {detail.engagement_codes.length
                        ? <>{t('linked to')} {detail.engagement_codes.map((c) => <span key={c} className="src-ref">{c} </span>)}</>
                        : t('not linked to any engagement')}
                    </span>
                  </div>
                  {/* The headline is the document's OWN name — the string the operator matches
                      against their disk and their inbox. The summary is the lead, once. */}
                  <h3 className="panel-slide-title" style={{ overflowWrap: 'anywhere' }}>{detail.original_filename}</h3>
                </div>
                <button className="panel-slide-close" onClick={() => setDetail(null)}>✕</button>
              </div>

              <div className="panel-slide-body">
                {detail.ai_summary ? (
                  <div className="read flex gap-8" style={{ marginBottom: 'var(--s20)' }}>
                    <span
                      className={detail.classification_held ? 'att att--held' : 'att att--proposed'}
                      title={detail.classification_held ? t('Classification held by you') : t('Classified by the librarian — not yet held')}
                    />
                    <div>
                      <Tx type="document" id={detail.id} field="ai_summary" original={detail.ai_summary} as="span" />
                      <TxTools type="document" id={detail.id} field="ai_summary" original={detail.ai_summary} />
                    </div>
                  </div>
                ) : (
                  <div className="read" style={{ marginBottom: 'var(--s20)' }}>
                    <span className="n-none">{t('The librarian has not summarised this document.')}</span>
                  </div>
                )}

                {/* ── YIELD, first. The one question the corpus exists to answer. ── */}
                <div className="docs-sec">
                  <div className="docs-sec-h">
                    <span className="u-label">{t('Yield')}</span>
                    <span className="zone-note">
                      {detail.evidence.length
                        ? t('{spans} in {reqs}', {
                          spans: tp(detail.evidence.length, '{n} span', '{n} spans'),
                          reqs: tp(new Set(detail.evidence.map((e) => e.requirement_id)).size, '{n} requirement', '{n} requirements'),
                        })
                        : detail.ever_covered
                          ? t('read by the agent, nothing cited')
                          : detail.ever_covered === false ? t('never opened by the agent') : t('coverage unknown')}
                    </span>
                  </div>
                  {detail.evidence.length ? (
                    <>
                      {detail.evidence.slice(0, EVIDENCE_SHOWN).map((e) => (
                        <EvidenceBlock
                          key={e.id}
                          e={e}
                          docRef={detail.ref}
                          onOpenRequirement={() => void openRoute(ROUTES.register({ engagement: engagementId, ref: e.requirement_ref }))}
                        />
                      ))}
                      {detail.evidence.length > EVIDENCE_SHOWN && (
                        <div className="docs-note">
                          <span className="voice-figure">{detail.evidence.length - EVIDENCE_SHOWN}</span> {t('further spans —')}{' '}
                          <button
                            className="link-btn"
                            onClick={() => void openRoute(ROUTES.register({ engagement: engagementId, document: detail.id }))}
                          >{t('open the register filtered to {ref}', { ref: detail.ref })}</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="read">
                      {t('Nothing in the register cites this document.')}{' '}
                      {detail.ever_covered
                        ? t('An extraction run has read it, so this is a finding, not a gap — the document carries no requirement.')
                        : detail.ever_covered === false
                          ? <><strong>{t('No extraction run has ever opened it.')}</strong> {t('Until one does, the register has not been checked against it.')}</>
                          : t('No run coverage is recorded for this engagement.')}
                      <div style={{ marginTop: 10 }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => void openRoute(ROUTES.agent({ engagement: engagementId, docs: [detail.id] }))}
                        >
                          {detail.ever_covered ? t('Run a targeted pass over {ref}', { ref: detail.ref }) : t('Queue {ref} for extraction', { ref: detail.ref })}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Holds on this one document ── */}
                {(!detail.effective_date_confirmed || hasOpenNearDuplicate(detail)
                  || (detail.near_duplicate_of?.length ?? 0) > 0 || detail.meta?.no_text_layer
                  || detail.extraction_error || (detail.meta?.skipped_attachments?.length ?? 0) > 0) && (
                  <div className="docs-sec">
                    <div className="docs-sec-h"><span className="u-label">{t('Holds')}</span></div>

                    {!detail.effective_date_confirmed && (
                      <div className="read text-sm" style={{ marginBottom: 10 }}>
                        {t('Effective date')} <span className="voice-figure">{fmtDate(detail.effective_date)}</span>{' '}
                        {detail.meta?.effective_date_detected_from
                          ? <>{t('detected from {source}', { source: detail.meta.effective_date_detected_from })}
                            {detail.meta.effective_date_pattern ? <> (<span className="src-ref">{detail.meta.effective_date_pattern}</span>)</> : null}</>
                          : t('not detected')} {t('— unconfirmed. The file mtime is never used.')}
                        {detail.effective_date && (
                          <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => void confirmDate(detail.id)}>{t('Confirm')}</button>
                        )}
                      </div>
                    )}

                    {detail.meta?.near_duplicate_hint && (
                      <div className="read text-sm" style={{ marginBottom: 10 }}>
                        {detail.meta.near_duplicate_hint.reason}
                        {detail.near_duplicate_resolved ? (
                          <span className="docs-tag docs-tag--ok" style={{ marginLeft: 8 }}>{t('decided')}</span>
                        ) : (
                          <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => { setPairsOpen(true); setDetail(null); }}>
                            {t('Decide the pair')}
                          </button>
                        )}
                      </div>
                    )}

                    {/* The other half of a pair used to show nothing at all. */}
                    {(detail.near_duplicate_of ?? []).map((n) => (
                      <div key={n.id} className="read text-sm" style={{ marginBottom: 10 }}>
                        <span className="src-ref">{n.ref}</span> {t('names this document as a near-duplicate:')} {n.reason}
                        <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => { setPairsOpen(true); setDetail(null); }}>
                          {t('Decide the pair')}
                        </button>
                      </div>
                    ))}

                    {detail.meta?.no_text_layer && (
                      <div className="read text-sm" style={{ marginBottom: 10 }}>
                        {detail.meta.no_text_reason ?? t('No text layer.')}
                        {canOcrFile(detail.original_filename) && (
                          <button
                            className="btn btn-sm" style={{ marginLeft: 8 }}
                            disabled={ocrQueue.has(detail.id) || ocrUp?.available === false}
                            title={ocrUp?.available === false ? ocrUp.reason ?? t('The vision model is unreachable') : undefined}
                            onClick={() => void runOcr(detail.id)}
                          >{ocrQueue.has(detail.id) ? tCtx(t, 'Reading…', 'ocr') : t('Read with OCR')}</button>
                        )}
                      </div>
                    )}

                    {detail.extraction_error && (
                      <div className="read text-sm" style={{ marginBottom: 10, color: 'var(--err)' }}>
                        {t('Extraction failed:')} {detail.extraction_error}
                      </div>
                    )}

                    {(detail.meta?.skipped_attachments?.length ?? 0) > 0 && (
                      <div className="read text-sm">
                        <span className="voice-figure">{detail.meta?.skipped_attachments?.length}</span>{' '}
                        {t('attachment(s) were unsupported at ingest:')}{' '}
                        <span className="src-ref">{(detail.meta?.skipped_attachments ?? []).join(', ')}</span>
                        <button
                          className="btn btn-sm" style={{ marginLeft: 8 }} disabled={rescanning}
                          onClick={() => void rescanAttachments(detail.id)}
                        >{rescanning ? t('Re-scanning…') : t('Re-scan attachments')}</button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Classification — the librarian's three fields, marked ── */}
                <div className="docs-sec">
                  <div className="docs-sec-h">
                    <span className="u-label">{t('Classification')}</span>
                    <span className="zone-note">
                      {detail.classification_held
                        ? t('held by you')
                        : <span className="tag-unheld">◇ {t('the librarian’s, not yet held')}</span>}
                    </span>
                  </div>
                  {editing ? (
                    <>
                      <div className="flex gap-12">
                        <div className="form-group" style={{ width: 190 }}>
                          <label className="form-label" htmlFor="doc-folder">{t('Form — the document’s kind')}</label>
                          <select id="doc-folder" className="form-select" value={editFolder} onChange={(e) => setEditFolder(e.target.value)}>
                            <option value="">{t('— unclassified —')}</option>
                            {DOCUMENT_FOLDERS.map((f) => <option key={f} value={f}>{t(f)}</option>)}
                          </select>
                        </div>
                        <div className="form-group flex-1">
                          <label className="form-label" htmlFor="doc-tags">{t('Subjects — comma-separated')}</label>
                          <input id="doc-tags" type="text" className="form-input" value={editTags} onChange={(e) => setEditTags(e.target.value)} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="doc-summary">{t('Summary — one line')}</label>
                        <textarea
                          id="doc-summary" className="form-textarea" rows={2} maxLength={500}
                          value={editSummary} onChange={(e) => setEditSummary(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="doc-eff-date">{t('Effective date — the date the content was true')}</label>
                        <input id="doc-eff-date" type="date" className="form-input mono" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                      </div>
                      <div className="flex gap-12">
                        <div className="form-group" style={{ width: 190 }}>
                          <label className="form-label" htmlFor="doc-source">{t('Source type')}</label>
                          <select id="doc-source" className="form-select" value={editSource} onChange={(e) => setEditSource(e.target.value as SourceType)}>
                            <option value="upload">{t('upload')}</option>
                            <option value="transcript">{t('transcript')}</option>
                            <option value="note">{t('note')}</option>
                            <option value="url">{t('url')}</option>
                          </select>
                        </div>
                        <div className="form-group flex-1">
                          <label className="form-label" htmlFor="doc-author">{t('Authored by')}</label>
                          <input id="doc-author" type="text" className="form-input" value={editAuthor} onChange={(e) => setEditAuthor(e.target.value)} />
                        </div>
                      </div>
                      <div className="flex gap-8">
                        <button className="btn btn-accent" onClick={() => void saveMetadata()}>{t('Save — and hold it')}</button>
                        <button className="btn btn-ghost" onClick={() => setEditing(false)}>{t('Cancel')}</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <dl className="docs-dl2">
                        <dt>{t('Form')}</dt><dd>{detail.folder ? t(detail.folder) : <span className="n-none">—</span>}</dd>
                        <dt>{t('Subjects')}</dt>
                        <dd>
                          {(detail.tags ?? []).length
                            ? (detail.tags ?? []).map((t) => <span key={t} className="docs-tag" style={{ marginRight: 4 }}>{t}</span>)
                            : <span className="n-none">—</span>}
                        </dd>
                      </dl>
                      <div className="docs-note">{t('The summary is the lead line at the top of this panel — stated once.')}</div>
                      <div className="flex gap-8 flex-wrap" style={{ marginTop: 12 }}>
                        {!detail.classification_held && (
                          <button className="btn btn-sm" onClick={() => void holdClassification(detail.id)}>{t('Hold this classification')}</button>
                        )}
                        <button className="btn btn-sm" onClick={() => setEditing(true)}>{t('Edit…')}</button>
                        {detail.has_text && (
                          <button
                            className="btn btn-sm" disabled={libQueue.has(detail.id)}
                            title={t('Ask the librarian to file it again — your edits always outrank the model')}
                            onClick={() => void reclassify(detail.id)}
                          >{libQueue.has(detail.id) ? t('Classifying…') : t('Re-run the librarian')}</button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* ── Provenance ── */}
                <div className="docs-sec">
                  <div className="docs-sec-h"><span className="u-label">{t('Provenance')}</span></div>
                  <dl className="docs-dl2">
                    <dt>{t('Effective')}</dt>
                    <dd className="voice-figure">
                      {fmtDate(detail.effective_date)}{' '}
                      {detail.effective_date_confirmed
                        ? <span className="docs-tag docs-tag--ok">{t('confirmed')}</span>
                        : <span className="docs-tag docs-tag--attend">{t('unconfirmed')}</span>}
                    </dd>
                    <dt>{t('Status')}</dt><dd>{t(detail.status)}</dd>
                    <dt>{t('Source')}</dt>
                    <dd>{t(detail.source_type)} · {t((detail.link_origin ?? detail.engagements.find((l) => l.id === engagementId)?.origin ?? 'uploaded_here').replace(/_/g, ' '))}</dd>
                    <dt>{t('Ingested')}</dt><dd className="voice-figure">{fmtDateTime(detail.ingested_at)}</dd>
                    <dt>{t('Size')}</dt>
                    <dd className="voice-figure">
                      {formatBytes(detail.byte_size)} · {t('{n} chars', { n: fmtNum(detail.text_length) })}
                      {detail.meta?.pages ? ` · ${t('{n} pages', { n: detail.meta.pages })}` : ''}
                    </dd>
                    <dt>MIME</dt><dd className="src-ref">{detail.mime_type ?? '—'}</dd>
                    <dt>sha256</dt><dd className="src-ref">{detail.sha256}</dd>
                    {detail.authored_by ? <><dt>{t('Authored by')}</dt><dd>{detail.authored_by}</dd></> : null}
                    {detail.superseded_by_ref ? <><dt>{t('Superseded by')}</dt><dd className="src-ref">{detail.superseded_by_ref}</dd></> : null}
                    {detail.supersedes.length ? (
                      <>
                        <dt>{t('Supersedes')}</dt>
                        <dd>
                          {detail.supersedes.map((s) => (
                            <button key={s.id} className="docs-tag" style={{ marginRight: 4 }} onClick={() => void openDetail(s.id)}>
                              <span className="src-ref">{s.ref}</span>
                            </button>
                          ))}
                        </dd>
                      </>
                    ) : null}
                    {duplicateCount(detail) > 0 ? (
                      <>
                        <dt>{t('Re-uploaded')}</dt>
                        <dd>
                          <span className="voice-figure">{duplicateCount(detail)}</span>× {t('— identical bytes arrived again and were not duplicated. Last:')} {fmtDateTime(detail.meta?.duplicate_uploads?.[duplicateCount(detail) - 1]?.at ?? null)}
                        </dd>
                      </>
                    ) : null}
                  </dl>

                  {/* Linked engagements: the document belongs to the CLIENT; these are the
                      engagements that use it. */}
                  <div style={{ marginTop: 12 }}>
                    <div className="docs-note" style={{ marginBottom: 4 }}>{t('Linked engagements')}</div>
                    {detail.engagements.length === 0 ? (
                      <div className="text-sm text-ink-3">{t('Not linked anywhere — this document lives only in the client library.')}</div>
                    ) : (
                      <table className="table table-compact">
                        <tbody>
                          {detail.engagements.map((l) => (
                            <tr key={l.id}>
                              <td className="src-ref" style={{ width: 100 }}>{l.code}</td>
                              <td className="text-xs text-ink-3" style={{ width: 140 }}>{l.origin === 'uploaded_here' ? t('uploaded here') : t('from library')}</td>
                              <td className="voice-figure text-xs text-ink-3" style={{ width: 120 }}>{fmtDateTime(l.linked_at)}</td>
                              <td className="text-xs text-ink-3">
                                {l.published && <span className="docs-tag docs-tag--ok">{t('portal')}</span>} {l.note ?? ''}
                              </td>
                              <td style={{ width: 90, textAlign: 'right' }}>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  onClick={() => setPendingUnlink({ docId: detail.id, ref: detail.ref, engagementId: l.id, engagementCode: l.code })}
                                ><Unlink className="icon-sm" /> {t('Unlink')}</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {detail.mime_type?.startsWith('image/') && (
                    <div style={{ marginTop: 12 }}>
                      <img
                        src={documentsApi.downloadUrl(detail.id)}
                        alt={detail.original_filename}
                        style={{
                          maxWidth: '100%', maxHeight: 340, objectFit: 'contain', display: 'block',
                          border: '1px solid var(--line)', borderRadius: 4, cursor: 'zoom-in',
                        }}
                        title={t('Open full size in a new tab')}
                        onClick={() => window.open(documentsApi.downloadUrl(detail.id), '_blank')}
                      />
                    </div>
                  )}

                  <div className="flex gap-8 flex-wrap" style={{ marginTop: 12 }}>
                    <a className="btn btn-sm" href={documentsApi.downloadUrl(detail.id)} download>
                      <Download className="icon-sm" /> {t('Download original')}
                    </a>
                    <button className="btn btn-sm" onClick={() => setShowText((v) => !v)}>
                      {showText ? t('Hide the extracted text') : t('Read the extracted text')}
                    </button>
                    {canOcrFile(detail.original_filename) && (
                      <button
                        className="btn btn-sm"
                        disabled={ocrQueue.has(detail.id) || ocrUp?.available === false}
                        title={ocrUp?.available === false ? ocrUp.reason ?? t('The vision model is unreachable') : undefined}
                        onClick={() => void runOcr(detail.id)}
                      >
                        <ScanText className="icon-sm" />
                        {ocrQueue.has(detail.id) ? tCtx(t, 'Reading…', 'ocr') : detail.meta?.ocr?.state === 'done' || detail.has_text ? t('Re-read with OCR') : t('Read with OCR')}
                      </button>
                    )}
                    {isEmailDoc(detail) && (
                      <button className="btn btn-sm" disabled={rescanning} onClick={() => void rescanAttachments(detail.id)}>
                        <RefreshCw className="icon-sm" /> {rescanning ? t('Re-scanning…') : t('Re-scan attachments')}
                      </button>
                    )}
                  </div>

                  {detail.meta?.ocr?.state === 'done' && (
                    <div className="docs-note" style={{ lineHeight: 1.5 }}>
                      {detail.meta.ocr.pages
                        ? t('This text was transcribed from the image by {model} across {n} page(s).', { model: detail.meta.ocr.model ?? t('the vision model'), n: detail.meta.ocr.pages })
                        : t('This text was transcribed from the image by {model}.', { model: detail.meta.ocr.model ?? t('the vision model') })}
                      {' '}{t('Evidence quoted from it is quoted from a machine transcription — check the original before it reaches a contract.')}
                      {detail.meta.ocr.truncated_pages
                        ? ` ${t('Only the first {pages} pages were read; {rest} remain.', { pages: detail.meta.ocr.pages, rest: detail.meta.ocr.truncated_pages })}`
                        : ''}
                    </div>
                  )}
                  {detail.meta?.ocr?.state === 'failed' && (
                    <div className="docs-note" style={{ color: 'var(--warn)' }}>{t('OCR failed:')} {detail.meta.ocr.error}</div>
                  )}

                  {showText && (
                    previewText ? (
                      <div style={{ marginTop: 12 }}>
                        {/* The client's document, verbatim — the record's own voice (LAW 1). */}
                        <pre
                          className="voice-record"
                          style={{
                            maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                            background: 'var(--bg-inset)', border: '1px solid var(--line)',
                            borderRadius: 'var(--r2)', padding: 'var(--s12)', margin: 0, maxWidth: 'none',
                          }}
                        >{truncated ? previewText.slice(0, PREVIEW_CHARS) : previewText}</pre>
                        {previewText.length > PREVIEW_CHARS && (
                          <button className="btn btn-sm mt-8" onClick={() => setShowAllText(!showAllText)}>
                            {truncated ? t('Show all {n} chars', { n: fmtNum(previewText.length) }) : t('Show first {n}', { n: fmtNum(PREVIEW_CHARS) })}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="docs-note">{detail.meta?.no_text_reason ?? t('This source produced no text layer.')}</div>
                    )
                  )}

                  {rescanReport && rescanReport.document_id === detail.id && (
                    <div style={{ marginTop: 12 }}>
                      <div className="docs-note" style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        {t('Attachment re-scan —')} <span className="voice-figure">{rescanReport.outcomes.length}</span>{' '}
                        {tp(rescanReport.outcomes.length, 'attachment', 'attachments')}
                        <button className="btn btn-sm btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => setRescanReport(null)}>{t('Dismiss')}</button>
                      </div>
                      <table className="table table-compact">
                        <tbody>
                          {rescanReport.outcomes.map((o, i) => (
                            <tr key={`${o.filename}-${i}`}>
                              <td style={{ width: 132 }}>
                                {o.outcome === 'created' && <span className="docs-tag docs-tag--ok">{t('created')}</span>}
                                {o.outcome === 'linked-existing' && <span className="docs-tag">{t('linked')}</span>}
                                {o.outcome === 'already-present' && <span className="docs-tag">{t('already present')}</span>}
                                {o.outcome === 'still-unsupported' && <span className="docs-tag docs-tag--attend">{t('unsupported')}</span>}
                                {o.outcome === 'failed' && <span className="docs-tag docs-tag--broken">{t('failed')}</span>}
                              </td>
                              <td className="src-ref" style={{ width: 86 }}>{o.ref ?? '—'}</td>
                              <td>
                                <span className="text-ink-1">{o.filename}</span>
                                {o.notice && <div className="text-xs text-ink-3">{o.notice}</div>}
                                {o.error && <div className="text-xs" style={{ color: 'var(--err)' }}>{o.error}</div>}
                              </td>
                              <td style={{ width: 74, textAlign: 'right' }}>
                                {o.document_id != null && <button className="btn btn-sm" onClick={() => void openDetail(o.document_id!)}>{t('Open')}</button>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* ── Disposals: three related acts, one fold, delete last ── */}
                  <details className="docs-fold" style={{ marginTop: 14 }}>
                    <summary>{t('Disposals — supersede, unlink, delete')}</summary>
                    <div className="docs-disposals">
                      <div className="read text-sm">
                        <strong>{t('Supersede')}</strong> {t('keeps a document that was real and has been overtaken — it keeps its evidence and its place in the chain.')} <strong>{t('Unlink')}</strong> {t('removes it from')}{' '}
                        <span className="src-ref">{current.code}</span> {t('only; it stays in the client library.')}
                        <strong> {t('Delete')}</strong> {t('is for one that should never have been ingested: the row and the file go, and only the audit entry survives.')}
                      </div>
                      {detail.supersession_chain.length > 1 && (
                        <div className="flex items-center gap-8 flex-wrap" style={{ marginTop: 10 }}>
                          {detail.supersession_chain.map((c, i) => (
                            <span key={c.id} className="flex items-center gap-8">
                              {i > 0 && <span className="text-ink-3">→</span>}
                              <button className="docs-tag" title={c.original_filename} onClick={() => c.id !== detail.id && void openDetail(c.id)}>
                                <span className="src-ref">{c.ref}</span>
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="form-group" style={{ marginTop: 10 }}>
                        <label className="form-label" htmlFor="doc-supersede">{t('Mark superseded by…')}</label>
                        <select id="doc-supersede" className="form-select" value={supersedeChoice} onChange={(e) => setSupersedeChoice(e.target.value)}>
                          <option value="">{t('— not superseded —')}</option>
                          {detail.siblings.map((s) => (
                            <option key={s.id} value={s.id}>{s.ref} · {s.effective_date ? fmtDate(s.effective_date) : t('no date')} · {s.original_filename}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-8 flex-wrap">
                        <button
                          className="btn btn-sm"
                          disabled={supersedeChoice === (detail.superseded_by_document_id ? String(detail.superseded_by_document_id) : '')}
                          onClick={() => {
                            if (!supersedeChoice) return void applySupersede(detail.id, null);
                            const s = detail.siblings.find((x) => String(x.id) === supersedeChoice);
                            setPendingSupersede({ id: detail.id, ref: s?.ref ?? supersedeChoice });
                          }}
                        >{t('Apply')}</button>
                        {detail.superseded_by_document_id && (
                          <button className="btn btn-sm btn-ghost" onClick={() => void applySupersede(detail.id, null)}>{t('Clear supersession')}</button>
                        )}
                        {engagementId && detail.engagements.some((l) => l.id === engagementId) && (
                          <button
                            className="btn btn-sm"
                            onClick={() => setPendingUnlink({
                              docId: detail.id, ref: detail.ref, engagementId, engagementCode: current.code,
                            })}
                          >{t('Unlink from {code}', { code: current.code })}</button>
                        )}
                        <button className="btn btn-sm btn-danger" onClick={() => void askDelete(detail.id, detail.ref)}>
                          <Trash2 className="icon-sm" /> {t('Delete {ref}', { ref: detail.ref })}
                        </button>
                      </div>
                    </div>
                  </details>
                </div>
              </div>
            </>
          )}
          {!detail && detailLoading && <div className="panel-slide-body"><div className="spinner" /></div>}
        </div>
      </div>

      {/* ── New note ─────────────────────────────────────────────────────── */}
      {noteOpen && (
        <div className="modal-overlay" onClick={() => setNoteOpen(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('New note')}</h2>
              <button className="close-btn" onClick={() => setNoteOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="note-title">{t('Title')}</label>
                <input id="note-title" className="form-input" placeholder={t('e.g. Toby — regulatory affairs scoping call')}
                  value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="note-date">{t('Effective date (optional)')}</label>
                <input id="note-date" type="date" className="form-input mono" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="note-body">{t('Body (markdown)')}</label>
                <textarea
                  id="note-body" className="form-textarea" rows={14} style={{ minHeight: 260 }}
                  placeholder={t('Notes become first-class evidence — requirements can cite a verbatim span of this text.')}
                  value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setNoteOpen(false)}>{t('Cancel')}</button>
              <button className="btn btn-accent" disabled={noteSaving || !noteTitle.trim() || !noteBody.trim()} onClick={() => void saveNote()}>
                {noteSaving ? t('Saving…') : t('Create note')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add from the client library ──────────────────────────────────── */}
      {pickerOpen && (
        <div className="modal-overlay" onClick={() => setPickerOpen(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('Add from the client library')}</h2>
              <button className="close-btn" onClick={() => setPickerOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="text-xs text-ink-3 mb-12">
                {t('These documents already exist in the client library and are not linked to')}{' '}
                <span className="src-ref">{current.code}</span>. {t('Linking makes them part of this engagement’s corpus — nothing is copied, and unlinking later leaves the document untouched.')}
              </p>
              <div className="toolbar">
                <div className="filter-chips" style={{ flexWrap: 'wrap' }}>
                  <button className={`chip${pickerFolder === '' ? ' active' : ''}`} onClick={() => setPickerFolder('')}>{t('All')}</button>
                  {DOCUMENT_FOLDERS.map((f) => (
                    <button key={f} className={`chip${pickerFolder === f ? ' active' : ''}`} onClick={() => setPickerFolder(f)}>{t(f)}</button>
                  ))}
                </div>
                <div className="search-wrapper">
                  <input type="text" className="search-input" placeholder={t('Search ref, filename, tag…')}
                    value={pickerQ} onChange={(e) => setPickerQ(e.target.value)} />
                  {pickerQ && <button className="search-clear" onClick={() => setPickerQ('')}>×</button>}
                </div>
              </div>
              {pickerLoading ? (
                <div className="flex justify-center p-24"><div className="spinner" /></div>
              ) : pickerRows.length === 0 ? (
                <EmptyState
                  kind={pickerFolder || pickerQ ? 'no-match' : 'empty'}
                  title={t('Nothing to link')}
                  detail={pickerFolder || pickerQ
                    ? t('No unlinked library documents match these filters.')
                    : t('Every document in the client library is already linked to {code}.', { code: current.code })}
                  onClear={pickerFolder || pickerQ ? () => { setPickerFolder(''); setPickerQ(''); } : undefined}
                  clearLabel={t('Clear the picker filters')}
                />
              ) : (
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  <table className="table table-compact">
                    <thead>
                      <tr>
                        <th style={{ width: 92 }}>{t('Ref')}</th>
                        <th>{t('Document')}</th>
                        <th style={{ width: 130 }}>{t('Form')}</th>
                        <th style={{ width: 160 }}>{t('Used by')}</th>
                        <th style={{ width: 80 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {pickerRows.map((r) => (
                        <tr key={r.id}>
                          <td className="src-ref">{r.ref}</td>
                          <td>
                            <span className="text-ink-1">
                              {r.ai_summary
                                ? <Tx type="document" id={r.id} field="ai_summary" original={r.ai_summary} as="span" />
                                : r.original_filename}
                            </span>
                            <div className="docs-file" title={r.original_filename}>{r.original_filename}</div>
                          </td>
                          <td className="text-xs text-ink-3">{r.folder ? t(r.folder) : t('unfiled')}</td>
                          <td>
                            <div className="flex gap-4 flex-wrap">
                              {r.engagements.length === 0
                                ? <span className="text-xs text-ink-3">{t('library only')}</span>
                                : r.engagements.map((l) => <span key={l.id} className="docs-tag"><span className="src-ref">{l.code}</span></span>)}
                            </div>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button className="btn btn-sm btn-accent" disabled={linkingId === r.id} onClick={() => void linkFromLibrary(r)}>
                              {linkingId === r.id ? t('Linking…') : t('Link')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setPickerOpen(false)}>{t('Done')}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!pendingSupersede}
        title={t('Mark as superseded')}
        confirmLabel={t('Mark superseded')}
        onCancel={() => setPendingSupersede(null)}
        onConfirm={() => {
          if (!pendingSupersede || !detail) return;
          if (pendingSupersede.id === detail.id) void applySupersede(detail.id, Number(supersedeChoice));
          else void applySupersede(pendingSupersede.id, detail.id);
        }}
      >
        <p className="text-sm text-ink-2">
          {t('The document keeps its evidence and stays downloadable — supersession is a status and a pointer, never a deletion. Recency arbitration will prefer the later document.')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={!!deleteTarget}
        title={deleteTarget?.plan.allowed ? t('Delete {ref}', { ref: deleteTarget.ref }) : t('{ref} cannot be deleted', { ref: deleteTarget?.ref ?? t('This document') })}
        confirmLabel={deleteTarget?.plan.allowed ? (deleting ? t('Deleting…') : t('Delete permanently')) : t('Supersede it instead')}
        danger={!!deleteTarget?.plan.allowed}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          if (deleteTarget.plan.allowed) return void runDelete();
          const id = deleteTarget.id;
          setDeleteTarget(null);
          void openDetail(id).then(() => {
            setTimeout(() => document.getElementById('doc-supersede')?.focus(), 120);
          });
        }}
      >
        {deleteTarget?.plan.allowed ? (
          <>
            <p className="text-sm text-ink-2">
              {t('Delete')} <span className="src-ref">{deleteTarget.ref}</span> — <span className="text-ink-1">{deleteTarget.plan.label}</span>?
              {' '}{t('The row and the file on disk go immediately. The audit log keeps the full record.')}
            </p>
            {deleteTarget.plan.cascade.length > 0 && (
              <ul className="text-sm text-ink-2 mt-8" style={{ paddingLeft: 18, lineHeight: 1.6 }}>
                {deleteTarget.plan.cascade.map((c) => (
                  <li key={c.what}><span className="voice-figure">{c.count}</span> {c.what} {t('go with it')}</li>
                ))}
              </ul>
            )}
            {!!deleteTarget.plan.linked_engagements?.length && (
              <p className="text-sm text-ink-2 mt-8" style={{ lineHeight: 1.6 }}>
                {t('It is linked into')} {deleteTarget.plan.linked_engagements.map((e) => <span className="src-ref" key={e.id}>{e.code} </span>)}
                {t('— every one of those engagements loses it from its corpus.')}
              </p>
            )}
            <p className="text-xs text-ink-3 mt-8">{t('This cannot be undone.')}</p>
          </>
        ) : (
          <>
            {deleteTarget?.plan.blockers.map((b) => (
              <p className="text-sm text-ink-2" key={b.code} style={{ lineHeight: 1.6 }}>{b.message}</p>
            ))}
            {!!deleteTarget?.plan.cited_by.length && (
              <div className="mt-12">
                <h4 className="section-label mb-8">{t('Cited by')}</h4>
                <table className="table table-compact">
                  <tbody>
                    {deleteTarget.plan.cited_by.map((r) => (
                      <tr key={r.id}>
                        <td className="src-ref" style={{ width: 92 }}>{r.ref}</td>
                        <td className="text-sm text-ink-2">{r.statement ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-ink-3 mt-8" style={{ lineHeight: 1.5 }}>
                  {t('Drop the evidence link on each of these in the Register and the document becomes deletable.')}
                </p>
              </div>
            )}
          </>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={!!pendingUnlink}
        title={t('Unlink {ref} from {code}', { ref: pendingUnlink?.ref, code: pendingUnlink?.engagementCode })}
        confirmLabel={t('Unlink')}
        onCancel={() => setPendingUnlink(null)}
        onConfirm={() => void runUnlink()}
      >
        <p className="text-sm text-ink-2" style={{ lineHeight: 1.6 }}>
          {t('The document stays in the client library and in every other engagement that links it — only')} <span className="src-ref">{pendingUnlink?.engagementCode}</span> {t('loses it from its corpus. The link’s note and its portal publish flag go with the link; re-linking later starts clean.')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={!!unlinkRefusal}
        title={t('{ref} cannot be unlinked from {code}', { ref: unlinkRefusal?.ref ?? t('This document'), code: unlinkRefusal?.engagementCode ?? t('this engagement') })}
        confirmLabel={t('Got it')}
        onCancel={() => setUnlinkRefusal(null)}
        onConfirm={() => setUnlinkRefusal(null)}
      >
        <p className="text-sm text-ink-2" style={{ lineHeight: 1.6 }}>{unlinkRefusal?.message}</p>
        {!!unlinkRefusal?.cited_by.length && (
          <div className="mt-12">
            <h4 className="section-label mb-8">{t('Cited by')}</h4>
            <table className="table table-compact">
              <tbody>
                {unlinkRefusal.cited_by.map((r) => (
                  <tr key={r.id}><td className="src-ref">{r.ref}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-ink-3 mt-8" style={{ lineHeight: 1.5 }}>
              {t('Drop the evidence link on each of these in the Register, then unlink.')}
            </p>
          </div>
        )}
      </ConfirmModal>
    </div>
  );
}

// ── the row ─────────────────────────────────────────────────────────────────
// ☐ · EFFECTIVE · REF · DOCUMENT · YIELD · HOLD. The columns are the decision
// inputs and nothing else. Size and Ingested moved to the panel's Provenance
// list; the Source badge became a clause on the form line (24 of 28 rows read
// "upload" — about 0.6 bits per row).

function DocRow({
  d, group, groupCount, maxSpans, selected, ocrQueued, libQueued, dateEdit,
  onToggleSel, onOpen, onStartDate, onChangeDate, onSaveDate, onCancelDate,
}: {
  d: DocumentRow;
  group: string | null;
  groupCount: number;
  maxSpans: number;
  selected: boolean;
  ocrQueued: boolean;
  libQueued: boolean;
  dateEdit: string | null;
  onToggleSel: () => void;
  onOpen: () => void;
  onStartDate: () => void;
  onChangeDate: (v: string) => void;
  onSaveDate: (v: string) => void;
  onCancelDate: () => void;
}) {
  const t = useT();
  const { tp, fmtDate, fmtNum } = useLocale();
  const spans = spansOf(d);
  const reqs = d.cited_by_requirements ?? 0;
  // The month group is composed in English (`monthOf`) so it can be compared; it is translated here.
  const groupLabel = group === null ? null
    : (() => { const m = /^([A-Za-z]+) (\d{4})$/.exec(group); return m ? `${t(m[1])} ${m[2]}` : t(group); })();

  // Only genuine holds. An unconfirmed date is NOT listed here: the date cell is already
  // attend-hued, and an emphasis that marks every row marks none (Laws of UX p.114).
  const holds: ReactNode[] = [];
  if (inPair(d)) holds.push(<span key="pair" className="docs-tag docs-tag--attend" title={d.meta?.near_duplicate_hint?.reason ?? d.near_duplicate_of?.[0]?.reason}>{t('pair open')}</span>);
  if (d.meta?.no_text_layer && !ocrQueued) holds.push(<span key="ocr" className="docs-tag docs-tag--attend" title={d.meta.no_text_reason}>{t('needs OCR')}</span>);
  if (ocrQueued) holds.push(<span key="reading" className="docs-tag" title={t('Reading with the vision model')}>{t('reading…')}</span>);
  if (libQueued) holds.push(<span key="filing" className="docs-tag" title={t('The librarian is filing this document')}>{t('filing…')}</span>);
  if (d.status === 'failed') holds.push(<span key="failed" className="docs-tag docs-tag--broken" title={d.extraction_error ?? ''}>{t('extraction failed')}</span>);
  if (d.superseded_by_ref) holds.push(<span key="sup" className="docs-tag">→ <span className="src-ref">{d.superseded_by_ref}</span></span>);
  if (d.meta?.skipped_attachments?.length) holds.push(<span key="att" className="docs-tag docs-tag--attend">{t('{n} attachment unread', { n: d.meta.skipped_attachments.length })}</span>);
  if (duplicateCount(d) > 0) holds.push(<span key="dup" className="docs-tag" title={t('Identical bytes arrived again and were not duplicated')}>{t('re-uploaded {n}×', { n: duplicateCount(d) })}</span>);

  return (
    <>
      {group && (
        <tr className="docs-group">
          <td colSpan={6}>
            <div className="docs-grouplab">
              <span className="u-label">{groupLabel}</span>
              <span className="zone-note voice-figure">{groupCount}</span>
            </div>
          </td>
        </tr>
      )}
      <tr
        className={`${selected ? 'docs-row--sel' : ''} ${d.status === 'superseded' ? 'docs-row--sup' : ''}`.trim()}
        onClick={onOpen}
      >
        <td className="docs-c-sel" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={selected} onChange={onToggleSel} />
        </td>
        <td className="docs-c-date" onClick={(e) => { if (dateEdit !== null || !d.effective_date) e.stopPropagation(); }}>
          {dateEdit !== null ? (
            <span className="flex items-center gap-4">
              <input
                type="date" className="form-input mono" autoFocus style={{ width: 140, padding: '2px 6px' }}
                value={dateEdit}
                onChange={(e) => onChangeDate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') onCancelDate();
                  if (e.key === 'Enter' && dateEdit) onSaveDate(dateEdit);
                }}
              />
              <button className="btn btn-sm btn-accent" disabled={!dateEdit} title={t('Save and confirm')}
                onClick={(e) => { e.stopPropagation(); onSaveDate(dateEdit); }}><Check className="icon-sm" /></button>
              <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); onCancelDate(); }}>×</button>
            </span>
          ) : d.effective_date ? (
            <span
              className={`voice-figure${d.effective_date_confirmed ? '' : ' docs-date-unc'}`}
              title={d.effective_date_confirmed
                ? t('Confirmed by the operator')
                : t('Detected from {source} — unconfirmed', { source: d.meta?.effective_date_detected_from ?? t('source') })}
            >{fmtDate(d.effective_date)}</span>
          ) : (
            <button className="link-btn" title={t('Type the date the content was true — saved and confirmed in one step')}
              onClick={(e) => { e.stopPropagation(); onStartDate(); }}>{t('set date')}</button>
          )}
        </td>
        <td className="docs-c-ref src-ref">{d.ref}</td>
        <td>
          {/* The librarian's one-line summary is the headline: the operator picks the first row
              that vaguely resembles what they want (Krug p.39), so the row must lead with
              meaning, not with an inbox artefact. 14 of 28 URSC filenames are the same email
              subject line. */}
          <div className="docs-sum">
            {d.ai_summary && (
              <span
                className={d.classification_held ? 'att att--held' : 'att att--proposed'}
                title={d.classification_held ? t('Classification held by you') : t('Classified by the librarian — not yet held')}
              />
            )}
            <span className="docs-sum-txt">
              {d.ai_summary
                ? <Tx type="document" id={d.id} field="ai_summary" original={d.ai_summary} as="span" />
                : d.original_filename}
            </span>
          </div>
          <div className="docs-file" title={d.original_filename}>{d.original_filename}</div>
          <div className="docs-form">
            {d.folder ? t(d.folder) : t('unfiled')}
            {d.source_type !== 'upload' ? ` · ${t(d.source_type)}` : ''}
            {d.meta?.pages ? ` · ${t('{n} pp', { n: d.meta.pages })}` : ''}
            {d.text_length ? ` · ${t('{n} chars', { n: fmtNum(d.text_length) })}` : ''}
          </div>
        </td>
        <td className="docs-c-yield">
          {/* Magnitude as LENGTH and ink weight, never a hue (ATTEST LAW 2) — Knaflic's heatmap
              argument, translated: the tails of the distribution at a glance, no colour spent. */}
          <div className="docs-yield">
            <span className="docs-ybar">{spans > 0 && <i style={{ width: `${Math.round((spans / maxSpans) * 100)}%` }} />}</span>
            <span className={`voice-figure${spans ? '' : ' n-none'}`}>{spans}</span>
          </div>
          <div className="docs-ydark">
            {spans
              ? tp(reqs, 'in {n} requirement', 'in {n} requirements')
              : d.ever_covered === false ? t('never opened') : d.ever_covered ? t('read, nothing cited') : t('coverage unknown')}
          </div>
        </td>
        <td className="docs-c-hold">
          <div className="docs-holds-cell">{holds.length ? holds : <span className="n-none">—</span>}</div>
        </td>
      </tr>
    </>
  );
}

// ── the pair decider ────────────────────────────────────────────────────────
// Both halves side by side with the deciding facts, EXACTLY two marks — later
// and longer — on the side that wins each, and both directions as peers. The
// suggestion is stated, never pre-applied (P1: AI proposes, human commits).

function PairCard({
  pair, onSupersede, onDismiss,
}: {
  pair: Pair;
  onSupersede: (loserId: number, winnerId: number) => Promise<void>;
  onDismiss: (id: number) => Promise<void>;
}) {
  const t = useT();
  const { fmtDate, fmtNum } = useLocale();
  const a = pair.a;
  const b = pair.b;
  const aDate = a.effective_date ?? '';
  const bDate = b?.effective_date ?? '';
  const aChars = a.text_length ?? 0;
  const bChars = b?.text_length ?? 0;
  const later = !b || aDate === bDate ? null : (aDate > bDate ? a.ref : pair.bRef);
  const longer = !b || aChars === bChars ? null : (aChars > bChars ? a.ref : pair.bRef);
  // A heuristic, stated as one: later AND longer is the shape of a revision.
  const suggest = later && later === longer ? later : null;

  const side = (ref: string, row: DocumentRow | null) => (
    <div className="docs-pair-side">
      <div className="docs-pair-h">
        <span className="src-ref" style={{ fontWeight: 600 }}>{ref}</span>
        <span className="voice-figure text-xs">{row?.effective_date ? fmtDate(row.effective_date) : <span className="n-none">{t('no date')}</span>}</span>
        {later === ref && <span className="docs-mark">{t('later')}</span>}
        {longer === ref && <span className="docs-mark">{t('longer')}</span>}
      </div>
      <div className="docs-pair-f" title={row?.original_filename}>{row?.original_filename ?? t('not linked to this engagement')}</div>
      <div className="docs-pair-m">
        <span><span className="voice-figure">{fmtNum(row?.text_length ?? 0)}</span> {t('chars')}</span>
        <span><span className="voice-figure">{fmtNum(Math.round((row?.byte_size ?? 0) / 1024))}</span> KB</span>
        <span>{row?.folder ? t(row.folder) : t('unfiled')}</span>
        {row && spansOf(row) > 0
          ? <span><span className="voice-figure">{spansOf(row)}</span> {t('spans cited')}</span>
          : <span className="n-none">{t('no spans cited')}</span>}
      </div>
    </div>
  );

  const verbA = (
    <button key="a" className="btn btn-sm" disabled={!b} onClick={() => b && void onSupersede(b.id, a.id)}>
      {t('{loser} superseded by {winner}', { loser: pair.bRef, winner: a.ref })}
    </button>
  );
  const verbB = (
    <button key="b" className="btn btn-sm" disabled={!b} onClick={() => b && void onSupersede(a.id, b.id)}>
      {t('{loser} superseded by {winner}', { loser: a.ref, winner: pair.bRef })}
    </button>
  );

  return (
    <div className="docs-pair">
      <div className="docs-pair-why">
        <span className="docs-tag">{t(pair.kind.replace(/_/g, ' '))}</span>
        <span>{pair.reason}</span>
      </div>
      <div className="docs-pair-grid">
        {side(a.ref, a)}
        {side(pair.bRef, b)}
      </div>
      <div className="docs-pair-verbs">
        {suggest === pair.bRef ? [verbB, verbA] : [verbA, verbB]}
        <button className="btn btn-sm btn-ghost" onClick={() => void onDismiss(a.id)}>{t('Not duplicates')}</button>
        <span className="docs-note" style={{ margin: '0 0 0 auto' }}>
          {suggest
            ? <><span className="src-ref">{suggest}</span> {t('is later and longer — first verb')}</>
            : b ? t('no ordering fact separates them') : t('the other half is not linked to this engagement')}
        </span>
      </div>
    </div>
  );
}

// ── one cited span, in full evidence dress ──────────────────────────────────
// serif behind the 2px rule, closed by its source line. An .ev without an
// .ev-src is a defect — and until Yield existed, this page never had to dress one.

function EvidenceBlock({ e, docRef, onOpenRequirement }: {
  e: DocumentEvidence;
  docRef: string;
  onOpenRequirement: () => void;
}) {
  const t = useT();
  const start = e.locator?.char_start;
  const end = e.locator?.char_end;
  return (
    <div className="docs-evblock">
      {/* The record's own line breaks are part of the record. */}
      <div className="ev" style={{ whiteSpace: 'pre-wrap' }}>{e.quoted_span}</div>
      <div className="ev-src">
        <span className="src-ref">{docRef}</span>
        {start != null && end != null && (
          <>
            <span className="sep">·</span>
            <span className="voice-figure">{t('chars {start}–{end}', { start, end })}</span>
          </>
        )}
        <span className="sep">·</span>
        <span>{t('cited by')}</span>
        <button className="link-btn src-ref" onClick={onOpenRequirement}>{e.requirement_ref}</button>
        <span className={claimMark(e.requirement_status)} title={t('Requirement status: {status}', { status: e.requirement_status })} />
      </div>
      {e.statement && (
        <div className="read text-sm" style={{ marginTop: 6, color: 'var(--ink-3)' }}>{e.statement}</div>
      )}
    </div>
  );
}
